import { supabase } from '../lib/supabase';
import { appLogService } from './appLogService';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const categoryMap = { frenos: 'refaccionaria', suspension: 'refaccionaria', motor: 'refaccionaria', otros: 'otros', llantas: 'llantera' };

export const quoteRequestService = {
  async getByWorkOrderId(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('quote_requests')
        ?.select('*, items:quote_request_items(*)')
        ?.eq('work_order_id', workOrderId)
        ?.maybeSingle();
      if (error) { console.error('[quoteRequestService] Error:', error); return null; }
      return data ? toCamelCase(data) : null;
    } catch (err) {
      console.error('[quoteRequestService] Failed getByWorkOrderId:', err);
      return null;
    }
  },

  async getPending(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('quote_requests')
        ?.select('*, items:quote_request_items(*), work_order:work_orders(folio_ot, estado_ot), client:clients(nombre), vehicle:vehicles(marca, modelo, year, placas)')
        ?.eq('company_id', companyId)
        ?.in('status', ['pending', 'sent'])
        ?.order('created_at', { ascending: false });
      if (error) { console.error('[quoteRequestService] Error getPending:', error); return []; }
      return (data || [])?.map(toCamelCase);
    } catch (err) {
      console.error('[quoteRequestService] Failed getPending:', err);
      return [];
    }
  },

  async createFromInspection({ workOrderId, inspectionId, companyId, clientId, vehicleId, inspectionDataByCategory }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data: existing } = await supabase
        ?.from('quote_requests')
        ?.select('id')
        ?.eq('work_order_id', workOrderId)
        ?.maybeSingle();

      let quoteRequestId;
      if (existing?.id) {
        quoteRequestId = existing?.id;
      } else {
        const { data: created, error: createErr } = await supabase
          ?.from('quote_requests')
          ?.insert({ company_id: companyId, work_order_id: workOrderId, inspection_id: inspectionId || null, client_id: clientId || null, vehicle_id: vehicleId || null, status: 'pending', created_by: user?.id || null })
          ?.select('id')
          ?.single();
        if (createErr) {
          console.error('[quoteRequestService] Error creating quote_request:', createErr);
          await appLogService?.error('quoteRequestService', 'Failed to create quote_request', { workOrderId, error: createErr?.message });
          return null;
        }
        quoteRequestId = created?.id;
      }

      const itemsToInsert = [];
      Object.entries(inspectionDataByCategory || {})?.forEach(([catKey, catData]) => {
        if (!catData?.findings && !catData?.parts?.length) return;
        const qrCategory = categoryMap?.[catKey] || 'otros';
        if (catData?.findings) {
          itemsToInsert?.push({
            company_id: companyId,
            quote_request_id: quoteRequestId,
            category: qrCategory,
            item_name: `[${catKey?.toUpperCase()}] ${catData?.findings?.substring(0, 120)}`,
            item_notes: catData?.recommendations || null,
            qty: 1,
            priority: catData?.status === 'critico' ? 'urgente' : catData?.status === 'malo' ? 'normal' : 'baja'
          });
        }
        (catData?.parts || [])?.forEach(part => {
          if (!part?.name) return;
          itemsToInsert?.push({
            company_id: companyId,
            quote_request_id: quoteRequestId,
            category: qrCategory,
            item_name: part?.name,
            item_notes: part?.notes || null,
            qty: part?.qty || 1,
            priority: catData?.status === 'critico' ? 'urgente' : 'normal',
            brand_preference: part?.brand || null
          });
        });
      });

      if (itemsToInsert?.length > 0) {
        const { data: existingItems } = await supabase
          ?.from('quote_request_items')
          ?.select('item_name, category')
          ?.eq('quote_request_id', quoteRequestId);
        const existingSet = new Set((existingItems || [])?.map(i => `${i.category}::${i.item_name}`));
        const newItems = itemsToInsert?.filter(i => !existingSet?.has(`${i?.category}::${i?.item_name}`));
        if (newItems?.length > 0) {
          const { error: itemsErr } = await supabase?.from('quote_request_items')?.insert(newItems);
          if (itemsErr) {
            console.error('[quoteRequestService] Error inserting items:', itemsErr);
            await appLogService?.error('quoteRequestService', 'Failed to insert quote_request_items', { quoteRequestId, error: itemsErr?.message });
          }
        }
      }
      return quoteRequestId;
    } catch (err) {
      console.error('[quoteRequestService] Failed createFromInspection:', err);
      await appLogService?.error('quoteRequestService', 'Unexpected error in createFromInspection', { workOrderId, error: err?.message });
      return null;
    }
  },

  async updateStatus(id, status) {
    try {
      const { error } = await supabase?.from('quote_requests')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('[quoteRequestService] Failed updateStatus:', err);
    }
  }
};
